import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useResolveFormSchema } from "../../../../../_hooks/useResolveFormSchema";
import fieldsToSections from "../../../../../_utils/fieldsToSections";
import { processUsageCompilationFormSchema } from "../utils/usageCompilationFormRules";

const COLUMN_FIELD_NAMES = ["consumptionColumn", "subscriptionIdColumn", "timestampColumn", "billingUnitIdColumn", "column"];
// These fields only make sense for schema-introspectable origins
// (MySQL/MongoDB/GSheets) - an "API LINK" source has no table to map, so
// these are hidden entirely rather than shown empty-and-required (see
// demo-bugs.md bug #41).
const DB_MAPPING_FIELD_NAMES = ["table", "sheet", ...COLUMN_FIELD_NAMES];

function withDynamicOptions(field, items, isLoading, emptyMessage) {
  return {
    ...field,
    items,
    isLoading,
    emptyMessage: emptyMessage || field.emptyMessage,
    allowNew: false,
    isObject: true,
    valueSelector: "value",
    valueDisplay: "label",
    searchSelector: ["value", "label"],
  };
}

/**
 * Builds UsageCompilationFormModal's field-schema sections from the org's
 * active "usageCompilation" object customizations, resolving any dynamic
 * (external reference) fields' data along the way - mirrors
 * _features/usageSources/modals/UsageSourceFormModal/hooks/
 * useUsageSourceFormSchema.js, extended for a 3-level cascade
 * (usageSource → table/sheet → column) instead of a single validate-gated
 * field.
 *
 * Ports processTableField/processSheetField/processColumnField/
 * generateTableOptions/generateSheetOptions/generateColumnOptions from the
 * old formSchemas/useUsageCompilationFormSchema.js verbatim - they already
 * produce the shared engine's field-descriptor shape (items/isObject/
 * valueSelector/valueDisplay/searchSelector), confirmed by reading the old
 * file, not guessed. Drops the old modal's *second*, redundant render-time
 * `field.options` override (the old renderInput engine's prop convention,
 * not this engine's - `field.items` set here already suffices).
 *
 * Wires the "Calculation" section's Reset button via the engine's existing
 * `section.btn` support (DynamicForm/index.jsx already renders it, no
 * engine change needed here - contrast Usage Sources' migration, which did
 * need one).
 */
export function useUsageCompilationFormSchema({
  sourceData,
  isCheckingSource,
  sourceInspectionError,
  selectedTable,
  selectedSheet,
  selectedUsageSource,
  onResetCalculation,
}) {
  const isApiLinkSource = selectedUsageSource?.origin === "API LINK";
  const objectCustomizations = useSelector(
    (state) => state?.objectCustomizationFeature?.objectCustomizations
  );

  const usageCompilationCustomization = useMemo(() => {
    const customization = objectCustomizations?.data?.usageCompilation;
    return Array.isArray(customization) ? customization : [];
  }, [objectCustomizations]);

  const baseSchema = useMemo(() => {
    const fields = usageCompilationCustomization.filter(
      (oc) => oc.status === "active" && oc.showDuringCreation
    );
    return fieldsToSections(fields);
  }, [usageCompilationCustomization]);

  const resolvedSchema = useResolveFormSchema(baseSchema);

  const columnOptions = useMemo(() => generateColumnOptions(sourceData, selectedTable, selectedSheet), [sourceData, selectedTable, selectedSheet]);

  const processedSchema = useMemo(() => {
    if (!Array.isArray(resolvedSchema)) return [];

    return resolvedSchema.map((section) => {
      const processedSection = {
        ...section,
        fields: (section.fields || [])
          .filter((field) => !isApiLinkSource || !DB_MAPPING_FIELD_NAMES.includes(field.name))
          .map((field) => {
            if (field.name === "table" && field.dataSourceType === "Dynamic Options") {
              return withDynamicOptions(
                field,
                generateTableOptions(sourceData),
                isCheckingSource,
                sourceInspectionError
              );
            }
            if (field.name === "sheet" && field.dataSourceType === "Dynamic Options") {
              return withDynamicOptions(
                field,
                generateSheetOptions(sourceData),
                isCheckingSource,
                sourceInspectionError
              );
            }
            if (COLUMN_FIELD_NAMES.includes(field.name) && field.dataSourceType === "Dynamic Options") {
              const disabled = field.name === "column" ? !selectedSheet : !selectedTable && !selectedSheet;
              return {
                ...withDynamicOptions(
                  field,
                  columnOptions,
                  isCheckingSource,
                  sourceInspectionError
                ),
                disabled,
              };
            }

            return field;
          }),
      };

      if (section.section === "Calculation") {
        processedSection.btn = { label: "Reset", onClick: onResetCalculation };
      }

      return processedSection;
    });
  }, [resolvedSchema, sourceData, isCheckingSource, sourceInspectionError, selectedTable, selectedSheet, columnOptions, onResetCalculation, isApiLinkSource]);

  return useMemo(() => processUsageCompilationFormSchema(processedSchema), [processedSchema]);
}

function generateTableOptions(sourceData) {
  if (!Array.isArray(sourceData)) return [];
  return sourceData.filter((item) => item.table).map((item) => ({ value: item.table, label: item.table }));
}

function generateSheetOptions(sourceData) {
  if (!Array.isArray(sourceData)) return [];
  return sourceData.filter((item) => item.sheet).map((item) => ({ value: item.sheet, label: item.sheet }));
}

function generateColumnOptions(sourceData, selectedTable, selectedSheet) {
  if (!Array.isArray(sourceData) || sourceData.length === 0) return [];

  const selectedTableValue = typeof selectedTable === "object" ? selectedTable?.value : selectedTable;
  const selectedSheetValue = typeof selectedSheet === "object" ? selectedSheet?.value : selectedSheet;

  let selectedItem = null;
  if (selectedTableValue || selectedSheetValue) {
    selectedItem = sourceData.find((item) => {
      if (item.table && selectedTableValue) return item.table === selectedTableValue;
      if (item.sheet && selectedSheetValue) return item.sheet === selectedSheetValue;
      return false;
    });
  }
  if (!selectedItem && sourceData.length > 0) {
    selectedItem = sourceData[0];
  }
  if (!selectedItem?.columns || !Array.isArray(selectedItem.columns)) return [];

  return selectedItem.columns.map((col) => {
    if (typeof col === "string") return { value: col, label: col };
    if (typeof col === "object" && col !== null) {
      const displayLabel = col.accessor || `${col.column}${col.row}` || col.value || "Unknown";
      return { value: col.accessor || `${col.column}${col.row}`, label: displayLabel };
    }
    const stringValue = String(col);
    return { value: stringValue, label: stringValue };
  });
}
